import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { CalendarDays, CalendarOff, ChevronLeft, ChevronRight, List, Plus, ShieldCheck } from "lucide-react";
import { get } from "@/lib/api";
import { errorMessage } from "@/lib/errors";
import { formatDate, formatTime } from "@/lib/format";
import { ALL, CONTENT_PLATFORMS, qs } from "@/lib/crm";
import { STATUS_CLASSES } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusPill } from "@/components/common/StatusPill";
import { AgentChip } from "@/components/common/AgentChip";
import { ErrorState, ListCard, SearchInput, TableSkeleton } from "@/components/common/DataStates";
import { FilterSelect, RowMenu, rowProps } from "@/pages/crm/shared";
import { ContentDialog } from "@/components/social/ContentDialog";
import { ContentSheet } from "@/components/social/ContentSheet";

/* ------------------------------------------------------------------------------------------------
 * Content Calendar — one record (content item), two views (Month grid / chronological List).
 * Single data source for both views: GET /api/content/calendar (same rows as /api/content/items).
 * "scheduled" only means planned in OneGate: nothing is ever published from here.
 * ---------------------------------------------------------------------------------------------- */

const VIEWS = ["month", "list"];
const RANGES = ["month", "week", "next7", "next30"];
const STATUS_OPTIONS = ["draft", "pending_approval", "approved", "scheduled", "cancelled", "idea", "published", "rejected"];
const LEGEND = ["draft", "pending_approval", "approved", "scheduled", "cancelled"];
const SOURCE_OPTIONS = ["manual", "agent"];
const PLATFORM_SHORT = { instagram: "IG", facebook: "FB", linkedin: "LI", tiktok: "TT", google_business: "GB", pinterest: "PI", newsletter: "NL", blog: "BL" };
const MAX_CHIPS = 3;
const STATUS_DOT = { idea: "bg-slate-400", draft: "bg-slate-400", pending_approval: "bg-amber-400", approved: "bg-emerald-500", scheduled: "bg-sky-500", published: "bg-emerald-700", rejected: "bg-rose-400", cancelled: "bg-slate-300", failed: "bg-rose-400" };

/* ---------- local-date helpers (the grid is rendered in the user's timezone) ---------- */
const pad = (n) => String(n).padStart(2, "0");
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const dayKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const monthKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
const parseMonth = (s) => { const m = /^(\d{4})-(\d{2})$/.exec(s || ""); return m && +m[2] >= 1 && +m[2] <= 12 ? new Date(+m[1], +m[2] - 1, 1) : null; };
const mondayOf = (d) => { const x = startOfDay(d); return addDays(x, -((x.getDay() + 6) % 7)); };
const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const gridDays = (month) => {
  const first = mondayOf(month);
  const last = addDays(mondayOf(new Date(month.getFullYear(), month.getMonth() + 1, 0)), 6);
  const days = [];
  for (let d = first; d <= last; d = addDays(d, 1)) days.push(d);
  return days;
};
const periodBounds = (view, range, month) => {
  const today = startOfDay(new Date());
  if (view === "month" || range === "month") { const days = gridDays(month); return { from: days[0], to: addDays(days[days.length - 1], 1) }; }
  if (range === "week") { const m = mondayOf(today); return { from: m, to: addDays(m, 7) }; }
  if (range === "next7") return { from: today, to: addDays(today, 7) };
  return { from: today, to: addDays(today, 30) };
};

const useIsMobile = () => {
  const [mobile, setMobile] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return mobile;
};

/* ---------- small presentational pieces ---------- */
const PlatformTag = ({ platform, className = "" }) => {
  const { t } = useTranslation();
  return <span className={cn("inline-flex shrink-0 items-center rounded-sm border border-current/20 bg-white/60 px-1 font-mono text-[10px] font-medium leading-4", className)} title={t(`platform.${platform}`, { defaultValue: platform })} aria-label={t(`platform.${platform}`, { defaultValue: platform })}>{PLATFORM_SHORT[platform] || platform.slice(0, 2).toUpperCase()}</span>;
};

/** Compact card used in the month grid and the "unscheduled" tray. Colour = status, plus the status label (never colour only). */
const ContentChip = ({ item, onOpen, compact = false }) => {
  const { t } = useTranslation();
  const statusLabel = t(`status.${item.status}`, { defaultValue: item.status });
  const platformLabel = t(`platform.${item.platform}`, { defaultValue: item.platform });
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onOpen(item.id); }}
      className={cn("og-focus flex w-full flex-col gap-0.5 rounded-md border px-1.5 py-1 text-left text-[11px] leading-4 transition-colors hover:brightness-95", STATUS_CLASSES[item.status] || STATUS_CLASSES.normal, compact && "max-w-[260px]")}
      title={`${item.title} — ${platformLabel} — ${statusLabel}${item.scheduled_at ? ` — ${formatTime(item.scheduled_at)}` : ""}`}
      aria-label={`${item.title}, ${platformLabel}, ${statusLabel}`}
      data-testid={`social-content-card-${item.id}`}
    >
      <span className="flex items-center gap-1">
        {item.scheduled_at && <span className="font-mono tabular-nums" data-testid={`social-content-card-time-${item.id}`}>{formatTime(item.scheduled_at)}</span>}
        <PlatformTag platform={item.platform} />
        <span className="truncate opacity-80" data-testid={`social-content-card-status-${item.id}`}>{statusLabel}</span>
        {item.approval_id && <ShieldCheck className="ml-auto h-3 w-3 shrink-0 opacity-70" aria-label={t("social.approval")} />}
      </span>
      <span className="truncate font-medium" data-testid={`social-content-card-title-${item.id}`}>{item.title}</span>
    </button>
  );
};

const SourceChip = ({ item }) => {
  const { t } = useTranslation();
  return item.source === "agent" ? <AgentChip agentId="CONTENT_AGENT" /> : <span className="text-xs text-muted-foreground">{t("social.manual")}</span>;
};

/* ---------- Month grid ---------- */
const MonthGrid = ({ month, itemsByDay, onOpenItem, onDayClick, onMore, canEdit, isMobile }) => {
  const { t } = useTranslation();
  const weekdays = t("social.weekdays", { returnObjects: true });
  const labels = Array.isArray(weekdays) && weekdays.length === 7 ? weekdays : ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
  const days = useMemo(() => gridDays(month), [month]);
  const todayKey = dayKey(new Date());
  return (
    <div role="grid" aria-label={t("social.views.month")} className="overflow-hidden" data-testid="social-calendar">
      <div role="row" className="grid grid-cols-7 border-b border-border bg-muted/40">
        {labels.map((l) => <div key={l} role="columnheader" className="px-2 py-1.5 text-center text-[11px] font-medium uppercase tracking-wide text-muted-foreground md:text-left">{l}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const key = dayKey(day);
          const items = itemsByDay.get(key) || [];
          const inMonth = day.getMonth() === month.getMonth();
          const isToday = key === todayKey;
          const handleCell = () => {
            if (isMobile) { if (items.length) onMore(day, items); else if (canEdit) onDayClick(day); return; }
            if (canEdit) onDayClick(day);
          };
          return (
            <div
              key={key}
              role="gridcell"
              aria-label={formatDate(day, { weekday: "long", day: "numeric", month: "long" })}
              onClick={handleCell}
              className={cn("group relative flex min-h-[64px] flex-col gap-1 border-b border-r border-border p-1 transition-colors md:min-h-[118px] md:p-1.5", canEdit && "cursor-pointer hover:bg-muted/40", !inMonth && "bg-muted/20", isToday && "bg-primary/5")}
              data-testid={`calendar-day-${key}`}
              data-in-month={inMonth ? "true" : "false"}
            >
              <div className="flex items-center justify-between">
                <span className={cn("inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium", isToday ? "bg-primary text-primary-foreground" : !inMonth ? "text-muted-foreground/60" : "text-foreground")} data-testid={isToday ? "calendar-today-cell" : undefined}>{day.getDate()}</span>
                {canEdit && !isMobile && (
                  <button type="button" onClick={(e) => { e.stopPropagation(); onDayClick(day); }} className="og-focus rounded p-0.5 text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100" aria-label={`${t("social.addOnDay")} — ${formatDate(day)}`} data-testid={`calendar-day-add-${key}`}>
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                )}
                {items.length > 0 && <span className="font-mono text-[10px] text-muted-foreground md:hidden" data-testid={`calendar-day-count-${key}`}>{items.length}</span>}
              </div>
              {/* mobile: status dots only (tap → day dialog) */}
              {items.length > 0 && (
                <div className="flex flex-wrap gap-0.5 md:hidden" aria-hidden="true">
                  {items.slice(0, 6).map((it) => <span key={it.id} className={cn("h-1.5 w-1.5 rounded-full", STATUS_DOT[it.status] || "bg-slate-400")} />)}
                </div>
              )}
              {/* desktop: compact chips */}
              <div className="hidden flex-col gap-1 md:flex">
                {items.slice(0, MAX_CHIPS).map((it) => <ContentChip key={it.id} item={it} onOpen={onOpenItem} />)}
                {items.length > MAX_CHIPS && (
                  <button type="button" onClick={(e) => { e.stopPropagation(); onMore(day, items); }} className="og-focus rounded px-1 text-left text-[11px] text-muted-foreground hover:text-foreground" data-testid={`calendar-day-more-${key}`}>{t("social.more", { count: items.length - MAX_CHIPS })}</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const GridSkeleton = () => (
  <div className="grid grid-cols-7" data-testid="social-loading" aria-busy="true">
    {Array.from({ length: 35 }).map((_, i) => <div key={i} className="min-h-[64px] border-b border-r border-border p-1.5 md:min-h-[118px]"><Skeleton className="h-5 w-6 rounded-full" />{i % 3 === 0 && <Skeleton className="mt-2 hidden h-8 w-full md:block" />}</div>)}
  </div>
);

/* ---------- Chronological list, grouped by day ---------- */
const ContentList = ({ groups, onOpenItem, onOpenApproval }) => {
  const { t } = useTranslation();
  return (
    <div className="overflow-x-auto" data-testid="social-list">
      <Table>
        <TableHeader className="og-table-head">
          <TableRow>
            <TableHead className="hidden w-20 sm:table-cell">{t("social.columns.time")}</TableHead>
            <TableHead>{t("social.columns.content")}</TableHead>
            <TableHead className="hidden md:table-cell">{t("social.columns.platform")}</TableHead>
            <TableHead>{t("social.columns.status")}</TableHead>
            <TableHead className="hidden lg:table-cell">{t("social.columns.approval")}</TableHead>
            <TableHead className="hidden lg:table-cell">{t("social.columns.source")}</TableHead>
            <TableHead className="hidden xl:table-cell">{t("social.columns.campaign")}</TableHead>
            <TableHead className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups.map(([key, items]) => (
            <React.Fragment key={key}>
              <TableRow className="bg-muted/40 hover:bg-muted/40" data-testid={`social-day-group-${key}`}>
                <TableCell colSpan={8} className="py-1.5 text-xs font-medium">
                  <span className="font-heading">{capitalize(formatDate(new Date(`${key}T12:00:00`), { weekday: "long", day: "numeric", month: "long", year: "numeric" }))}</span>
                  <span className="ml-2 font-mono text-[11px] text-muted-foreground">{t("social.count", { count: items.length })}</span>
                </TableCell>
              </TableRow>
              {items.map((it) => (
                <TableRow key={it.id} {...rowProps(() => onOpenItem(it.id), `social-list-row-${it.id}`)}>
                  <TableCell className="hidden whitespace-nowrap font-mono text-xs tabular-nums text-muted-foreground sm:table-cell" data-testid={`social-list-time-${it.id}`}>{it.scheduled_at ? formatTime(it.scheduled_at) : "—"}</TableCell>
                  <TableCell className="max-w-[210px] sm:max-w-[380px]"><div className="truncate font-medium" data-testid={`social-list-title-${it.id}`}>{it.title}</div>{(it.hook || it.body) && <div className="truncate text-xs text-muted-foreground">{it.hook || it.body}</div>}<div className="mt-1 flex flex-wrap items-center gap-1.5 md:hidden"><span className="font-mono text-[11px] tabular-nums text-muted-foreground sm:hidden">{it.scheduled_at ? formatTime(it.scheduled_at) : "—"}</span><Badge variant="outline" className="border-border bg-background text-[10px] font-normal">{t(`platform.${it.platform}`, { defaultValue: it.platform })}</Badge>{it.approval_status && <StatusPill status={it.approval_status} />}</div></TableCell>
                  <TableCell className="hidden md:table-cell"><div className="flex flex-wrap items-center gap-1"><Badge variant="outline" className="border-border bg-background text-xs font-normal" data-testid={`social-list-platform-${it.id}`}>{t(`platform.${it.platform}`, { defaultValue: it.platform })}</Badge><span className="text-xs text-muted-foreground">{t(`format.${it.format}`, { defaultValue: it.format })}</span></div></TableCell>
                  <TableCell><StatusPill status={it.status} testId={`social-list-status-${it.id}`} /></TableCell>
                  <TableCell className="hidden lg:table-cell">{it.approval_status ? <StatusPill status={it.approval_status} testId={`social-list-approval-${it.id}`} /> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="hidden lg:table-cell"><SourceChip item={it} /></TableCell>
                  <TableCell className="hidden max-w-[180px] truncate text-xs text-muted-foreground xl:table-cell">{it.campaign_name || "—"}</TableCell>
                  <TableCell className="text-right">
                    <RowMenu testId={`social-list-menu-${it.id}`} items={[
                      { key: "open", label: t("common.open"), onSelect: () => onOpenItem(it.id) },
                      { key: "approval", label: t("social.actions.openApproval"), onSelect: () => onOpenApproval(it.approval_id), hidden: !it.approval_id },
                    ]} />
                  </TableCell>
                </TableRow>
              ))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

/* ---------- Day dialog: all items of one day (from "+N more" or a mobile tap) ---------- */
const DayDialog = ({ day, items, onOpenChange, onOpenItem, onCreate, canEdit }) => {
  const { t } = useTranslation();
  return (
    <Dialog open={!!day} onOpenChange={(o) => !o && onOpenChange(null)}>
      <DialogContent className="og-scroll max-h-[85vh] overflow-y-auto bg-card sm:max-w-lg [&>*]:min-w-0" data-testid="social-day-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading" data-testid="social-day-dialog-title">{day ? t("social.dayItems", { date: formatDate(day, { weekday: "long", day: "numeric", month: "long" }) }) : ""}</DialogTitle>
          <DialogDescription>{t("social.scheduledNote")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          {(items || []).map((it) => (
            <button key={it.id} type="button" onClick={() => onOpenItem(it.id)} className="og-focus flex w-full items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-left text-sm transition-colors hover:bg-muted/40" data-testid={`social-day-dialog-item-${it.id}`}>
              <span className="w-12 shrink-0 font-mono text-xs tabular-nums text-muted-foreground">{it.scheduled_at ? formatTime(it.scheduled_at) : "—"}</span>
              <span className="min-w-0 flex-1 truncate font-medium">{it.title}</span>
              <Badge variant="outline" className="hidden border-border bg-card text-[10px] font-normal sm:inline-flex">{t(`platform.${it.platform}`, { defaultValue: it.platform })}</Badge>
              <StatusPill status={it.status} />
            </button>
          ))}
          {!items?.length && <p className="text-sm text-muted-foreground">{t("social.empty")}</p>}
        </div>
        {canEdit && (
          <DialogFooter>
            <Button variant="outline" className="bg-card" onClick={() => { onOpenChange(null); onCreate(day); }} data-testid="social-day-dialog-new"><Plus className="mr-2 h-4 w-4" />{t("social.addOnDay")}</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* ------------------------------------------------------------------------------------------------ */
export default function ContentCalendarPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { orgId, hasRole } = useAuth();
  const canEdit = hasRole("member");
  const isMobile = useIsMobile();
  const [params, setParams] = useSearchParams();

  // URL-driven state: view, month, opened item (so reload / shared links restore the same screen)
  const view = VIEWS.includes(params.get("view")) ? params.get("view") : isMobile ? "list" : "month";
  const month = useMemo(() => parseMonth(params.get("month")) || new Date(new Date().getFullYear(), new Date().getMonth(), 1), [params]);
  const selectedId = params.get("item");
  const setParam = (key, value, replace = false) => setParams((prev) => { const p = new URLSearchParams(prev); if (value) p.set(key, value); else p.delete(key); return p; }, { replace });

  // local filters
  const [status, setStatus] = useState(ALL);
  const [platform, setPlatform] = useState(ALL);
  const [source, setSource] = useState(ALL);
  const [range, setRange] = useState("month");
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(null); // { scheduled_at } | null
  const [dayOpen, setDayOpen] = useState(null); // { day, items }

  const bounds = useMemo(() => periodBounds(view, range, month), [view, range, month]);
  const query = qs({ date_from: bounds.from.toISOString(), date_to: new Date(bounds.to.getTime() - 1).toISOString(), platform, status });
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({ queryKey: ["content", "calendar", orgId, query], queryFn: () => get(`/content/calendar${query}`), enabled: !!orgId, placeholderData: (prev) => prev });

  // Client-side refinement (source / search) on the same dataset for both views; grouping by *local* day.
  // In List view the "displayed month" range is the calendar month itself (the grid also shows adjacent days).
  const monthStart = useMemo(() => new Date(month.getFullYear(), month.getMonth(), 1), [month]);
  const monthEnd = useMemo(() => new Date(month.getFullYear(), month.getMonth() + 1, 1), [month]);
  const inMonth = (it) => new Date(it.scheduled_at) >= monthStart && new Date(it.scheduled_at) < monthEnd;
  const inListPeriod = (it) => view !== "list" || range !== "month" || inMonth(it);
  const matches = (it) => (source === ALL || it.source === source || (source === "manual" && it.source === "import")) && (!q || `${it.title} ${it.hook || ""} ${it.body || ""}`.toLowerCase().includes(q.toLowerCase()));
  const scheduledItems = useMemo(() => (data?.days || []).flatMap((d) => d.items).filter((it) => matches(it) && inListPeriod(it)).sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at)), [data, source, q, view, range, monthStart, monthEnd]); // eslint-disable-line react-hooks/exhaustive-deps
  const itemsByDay = useMemo(() => { const m = new Map(); scheduledItems.forEach((it) => { const k = dayKey(new Date(it.scheduled_at)); if (!m.has(k)) m.set(k, []); m.get(k).push(it); }); return m; }, [scheduledItems]);
  const groups = useMemo(() => [...itemsByDay.entries()].sort(([a], [b]) => a.localeCompare(b)), [itemsByDay]);
  const unscheduled = useMemo(() => (data?.unscheduled || []).filter((it) => matches(it) && (platform === ALL || it.platform === platform) && (status === ALL || it.status === status)), [data, source, q, platform, status]); // eslint-disable-line react-hooks/exhaustive-deps
  const legend = useMemo(() => { const present = new Set(scheduledItems.map((i) => i.status)); return [...LEGEND, ...STATUS_OPTIONS.filter((s) => !LEGEND.includes(s) && present.has(s))]; }, [scheduledItems]);

  const openItem = (id) => setParam("item", id);
  const openApproval = (approvalId) => approvalId && navigate(`/app/approvals?status=all&item=${approvalId}`);
  const createOn = (day) => setCreating({ scheduled_at: day ? `${dayKey(day)}T09:00` : "" });
  const goMonth = (delta) => setParam("month", monthKey(new Date(month.getFullYear(), month.getMonth() + delta, 1)), true);
  const goToday = () => { setRange("month"); setParam("month", monthKey(new Date()), true); };

  const periodLabel = view === "month" || range === "month"
    ? capitalize(formatDate(month, { month: "long", year: "numeric" }))
    : `${formatDate(bounds.from, { day: "numeric", month: "short" })} – ${formatDate(addDays(bounds.to, -1), { day: "numeric", month: "short", year: "numeric" })}`;
  const showMonthNav = view === "month" || range === "month";
  const isEmpty = !isLoading && !isError && scheduledItems.length === 0;
  // The counter always describes the displayed period (calendar month in both views; grid cells of adjacent months are shown muted but not counted).
  const periodCount = view === "month" ? scheduledItems.filter(inMonth).length : scheduledItems.length;

  return (
    <div className="space-y-6" data-testid="social-page">
      <PageHeader title={t("social.title")} subtitle={t("social.subtitle")} testId="social-header" actions={canEdit && <Button onClick={() => createOn(null)} data-testid="social-new-content"><Plus className="mr-2 h-4 w-4" />{t("social.newContent")}</Button>} />

      <Alert className="border-sky-200 bg-sky-50 text-sky-900" data-testid="social-banner">
        <ShieldCheck className="h-4 w-4 !text-sky-900" aria-hidden="true" />
        <AlertTitle className="font-heading text-sm">{t("social.approvedNotExecuted")}</AlertTitle>
        <AlertDescription className="text-xs leading-5">{t("social.scheduledNote")} {canEdit && <span className="hidden md:inline">{t("social.clickDayHint")}</span>}{isMobile && <span> {t("social.mobileHint")}</span>}</AlertDescription>
      </Alert>

      <ListCard
        testId="social-calendar-card"
        refreshing={isFetching && !isLoading}
        toolbar={
          <>
            <div className="flex w-full flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <Tabs value={view} onValueChange={(v) => setParam("view", v, true)}>
                  <TabsList className="bg-muted p-1" data-testid="social-calendar-view-tabs">
                    <TabsTrigger value="month" data-testid="social-view-month"><CalendarDays className="mr-1.5 h-3.5 w-3.5" />{t("social.views.month")}</TabsTrigger>
                    <TabsTrigger value="list" data-testid="social-view-list"><List className="mr-1.5 h-3.5 w-3.5" />{t("social.views.list")}</TabsTrigger>
                  </TabsList>
                </Tabs>
                {showMonthNav && (
                  <div className="flex items-center gap-1" data-testid="social-month-nav">
                    <Button variant="outline" size="icon" className="h-9 w-9 bg-card" onClick={() => goMonth(-1)} aria-label={t("social.prevMonth")} data-testid="social-prev-month"><ChevronLeft className="h-4 w-4" /></Button>
                    <span className="min-w-[150px] px-1 text-center font-heading text-sm font-semibold md:text-base" data-testid="social-period-label" aria-live="polite">{periodLabel}</span>
                    <Button variant="outline" size="icon" className="h-9 w-9 bg-card" onClick={() => goMonth(1)} aria-label={t("social.nextMonth")} data-testid="social-next-month"><ChevronRight className="h-4 w-4" /></Button>
                    <Button variant="outline" size="sm" className="ml-1 bg-card" onClick={goToday} data-testid="social-today">{t("social.today")}</Button>
                  </div>
                )}
                {!showMonthNav && <span className="px-1 font-heading text-sm font-semibold md:text-base" data-testid="social-period-label">{periodLabel}</span>}
              </div>
              <span className="text-xs text-muted-foreground" data-testid="social-count">{t("social.count", { count: periodCount })} {t("social.inPeriod")}{unscheduled.length > 0 && <> · {unscheduled.length} {t("social.unscheduled").toLowerCase()}</>}</span>
            </div>
            <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              <SearchInput value={q} onChange={setQ} placeholder={t("social.searchPlaceholder")} className="sm:w-60" testId="social-search" />
              <FilterSelect value={status} onChange={setStatus} options={STATUS_OPTIONS} allLabel={t("social.allStatuses")} render={(o) => t(`status.${o}`, { defaultValue: o })} testId="social-filter-status" className="w-full sm:w-[170px]" />
              <FilterSelect value={platform} onChange={setPlatform} options={CONTENT_PLATFORMS} allLabel={t("social.allPlatforms")} render={(o) => t(`platform.${o}`, { defaultValue: o })} testId="social-filter-platform" className="w-full sm:w-[190px]" />
              <FilterSelect value={source} onChange={setSource} options={SOURCE_OPTIONS} allLabel={t("social.filters2.allSources")} render={(o) => (o === "agent" ? <span className="inline-flex items-center gap-1.5"><AgentChip agentId="CONTENT_AGENT" />{t("social.filters2.agent")}</span> : t("social.filters2.human"))} testId="social-filter-source" className="w-full sm:w-[220px]" />
              {view === "list" && <FilterSelect value={range === "month" ? ALL : range} onChange={(v) => setRange(v === ALL ? "month" : v)} options={RANGES.filter((r) => r !== "month")} allLabel={t("social.range.month")} render={(o) => t(`social.range.${o}`)} testId="social-filter-range" className="w-full sm:w-[190px]" />}
            </div>
          </>
        }
        footer={
          !isLoading && !isError && unscheduled.length > 0 && (
            <div className="border-t border-border bg-background px-4 py-3" data-testid="social-unscheduled">
              <div className="mb-2 flex flex-wrap items-center gap-2"><CalendarOff className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" /><span className="og-section-label">{t("social.unscheduled")}</span><span className="font-mono text-[11px] text-muted-foreground">({unscheduled.length})</span><span className="text-xs text-muted-foreground">{t("social.unscheduledHint")}</span></div>
              <div className="flex flex-wrap gap-1.5">{unscheduled.map((it) => <ContentChip key={it.id} item={it} onOpen={openItem} compact />)}</div>
            </div>
          )
        }
      >
        {isError && <div className="p-4"><ErrorState message={errorMessage(error)} onRetry={refetch} testId="social-error" /></div>}
        {isLoading && !isError && (view === "month" ? <GridSkeleton /> : <TableSkeleton testId="social-loading" />)}
        {!isLoading && !isError && view === "month" && (
          <>
            <MonthGrid month={month} itemsByDay={itemsByDay} onOpenItem={openItem} onDayClick={createOn} onMore={(day, items) => setDayOpen({ day, items })} canEdit={canEdit} isMobile={isMobile} />
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-border px-4 py-2.5" data-testid="social-legend">
              <span className="og-section-label">{t("social.status")}</span>
              {legend.map((s) => <StatusPill key={s} status={s} testId={`social-legend-${s}`} />)}
              {isEmpty && <span className="ml-auto text-xs text-muted-foreground" data-testid="social-empty">{t("social.empty")} {canEdit && <button type="button" className="og-focus rounded font-medium text-primary hover:underline" onClick={() => createOn(null)} data-testid="social-empty-new">{t("social.newContent")}</button>}</span>}
            </div>
          </>
        )}
        {!isLoading && !isError && view === "list" && (groups.length > 0
          ? <ContentList groups={groups} onOpenItem={openItem} onOpenApproval={openApproval} />
          : <div className="p-4"><EmptyState icon={CalendarDays} title={t("social.listEmpty")} hint={t("social.emptyHint")} className="border-dashed shadow-none" testId="social-empty" action={canEdit && <Button size="sm" variant="outline" className="bg-card" onClick={() => createOn(null)} data-testid="social-empty-new"><Plus className="mr-2 h-4 w-4" />{t("social.newContent")}</Button>} /></div>
        )}
      </ListCard>

      <ContentSheet id={selectedId} onClose={() => setParam("item", null)} />
      <ContentDialog open={!!creating} onOpenChange={(o) => !o && setCreating(null)} defaults={creating || {}} onSaved={(d) => setParams((prev) => { const p = new URLSearchParams(prev); if (d?.scheduled_at) p.set("month", monthKey(new Date(d.scheduled_at))); if (d?.id) p.set("item", d.id); return p; })} />
      <DayDialog day={dayOpen?.day} items={dayOpen?.items} onOpenChange={setDayOpen} onOpenItem={(id) => { setDayOpen(null); openItem(id); }} onCreate={createOn} canEdit={canEdit} />
    </div>
  );
}
